import React, { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Helmet } from 'react-helmet-async';
import { debounce } from 'lodash';
import Faq from '../../components/Faq/Faq';
import BusDetailsList from '../../components/BusDetailsList/BusDetailsList';
import Footer from '../../components/Footer/Footer';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import PageHeader from '../../components/PageHeader/PageHeader';
import StarRatingBox from '../../components/StarRatingBox/StarRatingBox';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import BusModifySearch from '../../components/BusModifySearch/BusModifySearch';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import AppContextProvider from '../../AppContext';
import ReturnBusSection from '../../components/ReturnBusSection';
import { RouteComponentProps } from 'react-router-dom';
import '../RouteTemplate/RouteTemplate.css';
import {
  RouteOperatorTemplateDataChecker,
  RouteOperatorData,
} from './RouteOperatorDataTypeChecker';
import { ThrowReporter } from 'io-ts/lib/ThrowReporter';
import { trackRouteOperatorTemplateEvent } from '../../utils/omnitureTracking';
import { EnvironmentVariables } from '../../types';
import { getAppEnv } from '../../../config/env';
import AdsComponent from '../../components/AdsComponent';

import OfferDropdown from '../../components/OfferDropdown/OfferDropdown';
import CompactListV1 from '../../components/CompactList/CompactListV1';
import SchemaInformation, {
  SchemaInformationTypes,
} from '../../components/SchemaInformation/SchemaInformation';
import { yieldToMainThread } from '../../utils/scheduler';
import { BusBookingSummary } from '../../components/BusBookingSummary';

type PathParamsType = {
  fromCityCode: string;
  toCityCode: string;
};
type RouteOperatorTemplateProp = RouteComponentProps<PathParamsType> & {
  data: RouteOperatorData;
};
type MousePosType = {
  x: number;
  y: number;
};
const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;

const runtimeTypChecker = (data) => {
  const result = RouteOperatorTemplateDataChecker.decode(data);
  if (result._tag !== 'Right') {
    // console.log(PathReporter.report(result));
    ThrowReporter.report(result);
  }
};

const RouteOperatorTemplate = ({ data }: RouteOperatorTemplateProp) => {
  const {
    routeData,
    operatorData,
    rtcAcronym,
    nearbyCitiesSource,
    nearbyCitiesDestination,
    seoOfferData,
    schemaInfo,
  } = data;

  const {
    topOperatorsList,
    sourceCityData,
    destinationCityData,
    topBusesFrom,
    returnRouteOperatorUrl,
  } = routeData;
  const { templateData, offers, bookingSummary } = data;
  const { name: sourceCityName } = sourceCityData;
  const { name: destinationCityName } = destinationCityData;
  const { code: sourceCityCode } = sourceCityData;
  const { code: destinationCityCode } = destinationCityData;
  const operatorName = operatorData.operatorName;
  const displayedOperatorName = rtcAcronym ? rtcAcronym : operatorName;
  const _mqMobile = useMediaQuery({
    query: '(max-device-width: 768px)',
  });
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  const isMobileDevice = isHydrated ? _mqMobile : false;
  const [collapsableFlag, setcollapsableFlag] = useState(false);
  const [fromCity, setFromCity] = useState(
    sourceCityName && sourceCityCode ? sourceCityName : '',
  );
  const [toCity, setToCity] = useState(
    destinationCityName && destinationCityCode ? destinationCityName : '',
  );
  const [fromCode, setFromCode] = useState(
    sourceCityName && sourceCityCode ? sourceCityCode : '',
  );
  const [toCode, setToCode] = useState(
    destinationCityName && destinationCityCode ? destinationCityCode : '',
  );
  const [widget, setWidget] = useState<null | number>(null);
  const [funnelUrl, setFunnelUrl] = useState<'' | string>('');

  const date = new Date();
  date.setDate(date.getDate() + 1);

  const onBusClicked = (funnelUrl?: string) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        onFromChange(sourceCityName, sourceCityCode);
        onToChange(destinationCityName, destinationCityCode);
        funnelUrl && setFunnelUrl(funnelUrl);
      });
    });
  };

  const [mousePos, setMousePos] = useState({} as MousePosType);
  const [openOffersDropdown, setOpenOffersDropdown] = useState(false);
  const [hasDropdownOpened, setHasDropdownOpened] = useState(false);

  useEffect(() => {
    if (seoOfferData?.length > 0) {
      const handleMouseMove = debounce((event) => {
        if (!hasDropdownOpened) {
          setMousePos({ x: event.clientX, y: event.clientY });
        }
      }, 250);

      window.addEventListener('mousemove', handleMouseMove);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, []);

  useEffect(() => {
    if (
      Object.keys(mousePos).length > 0 &&
      mousePos.y <= 20 &&
      !hasDropdownOpened
    ) {
      setOpenOffersDropdown(true);
      setHasDropdownOpened(true);
    }
  }, [mousePos]);

  const onFromChange = (city, code) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setFromCity(city);
        setFromCode(code);
      });
    });
    trackRouteOperatorTemplateEvent(`Bus_SEO_Route_Operator_From_Select`);
  };

  const onToChange = (city, code) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setToCity(city);
        setToCode(code);
      });
    });
    trackRouteOperatorTemplateEvent(`Bus_SEO_Route_Operator_To_Select`);
  };

  const getListingUrl = (): string => {
    return isMobileDevice
      ? `https://mbus.makemytrip.com/rnw/busListingPage?from=${fromCity}&from_code=${fromCode}&to=${toCity}&to_code=${toCode}&departure=travelDate&sort=Rating&`
      : `https://www.makemytrip.com/bus/search/${fromCity}/${toCity}/travelDate?from_city=${fromCode}&to_city=${toCode}&`;
  };

  useEffect(() => {
    const seeGuildLinesElement = document.getElementById('SeeGuidelines');
    seeGuildLinesElement &&
      seeGuildLinesElement.addEventListener('click', () => {
        trackRouteOperatorTemplateEvent(
          `Bus_SEO_RouteOperatorTemplate_MySafety_See_GuideLines_Click`,
        );
      });

    const learnMoreElement = document.getElementById('LearnMore');
    learnMoreElement &&
      learnMoreElement.addEventListener('click', () => {
        trackRouteOperatorTemplateEvent(
          `Bus_SEO_RouteOperatorTemplate_MySafety_Learn_More_Click`,
        );
      });
  }, []);

  if (data.Show404) {
    return (
      <div style={{ textAlign: 'center' }}>
        <h1>404</h1>
        <h2>Sorry, page not found</h2>
      </div>
    );
  }

  const srcBoardingPointsBlockData = {
    mainData: sourceCityData?.cityBoardingPointsList,
    titlePrefix: 'Boarding Points',
    titleSuffix: ` in ${sourceCityName}`,
  };

  const srcOtherRoutesFromBlockData = {
    mainData: sourceCityData?.otherRoutesFrom,
    titlePrefix: 'Other routes',
    titleSuffix: ` from ${sourceCityName}`,
  };

  const srcOtherRoutesToBlockData = {
    mainData: sourceCityData?.otherRoutesTo,
    titlePrefix: 'Other routes',
    titleSuffix: ` to ${sourceCityName}`,
  };

  const otherRoutesOperatorBlockData = {
    mainData: sourceCityData?.otherRouteOperators,
    titlePrefix: `Other ${displayedOperatorName} buses`,
    titleSuffix: ` from ${sourceCityName}`,
  };

  const nearbyCitiesBlockData = {
    mainData: nearbyCitiesSource,
    titlePrefix: `Popular Cities near `,
    titleSuffix: sourceCityName,
  };

  const sourceInterlinkingBlockData = [
    srcBoardingPointsBlockData,
    srcOtherRoutesFromBlockData,
    srcOtherRoutesToBlockData,
    otherRoutesOperatorBlockData,
    nearbyCitiesBlockData,
  ];

  const desDroppingPointsBlockData = {
    mainData: destinationCityData?.cityDropPointsList,
    titlePrefix: 'Dropping Points',
    titleSuffix: ` in ${destinationCityName}`,
  };

  const desOtherRoutesFromBlockData = {
    mainData: destinationCityData?.otherRoutesFrom,
    titlePrefix: 'Other routes',
    titleSuffix: ` from ${destinationCityName}`,
  };

  const desOtherRoutesToBlockData = {
    mainData: destinationCityData?.otherRoutesTo,
    titlePrefix: 'Other routes',
    titleSuffix: ` to ${destinationCityName}`,
  };

  const desOtherRouteOperatorsBlockData = {
    mainData: destinationCityData?.otherRouteOperators,
    titlePrefix: `Other ${displayedOperatorName} buses`,
    titleSuffix: ` from ${destinationCityName}`,
  };

  const destinationPopularCitiesBlockData = {
    mainData: nearbyCitiesDestination,
    titlePrefix: `Popular Cities near `,
    titleSuffix: destinationCityName,
  };

  const destinationInterlinkingBlockData = [
    desDroppingPointsBlockData,
    desOtherRoutesFromBlockData,
    desOtherRoutesToBlockData,
    desOtherRouteOperatorsBlockData,
    destinationPopularCitiesBlockData,
  ];

  return (
    <AppContextProvider
      omniUrlArgs={'source=SEO_Route_Operator_Template'}
      trackTemplateEvent={trackRouteOperatorTemplateEvent}
    >
      {schemaInfo && (
        <SchemaInformation
          schemaInformation={schemaInfo as SchemaInformationTypes}
          templateData={templateData}
        />
      )}
      <div className={`busSeoPage`}>
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
        </Helmet>
        <div className={`desktopShow makeSticky`}>
          <BusModifySearch
            funnelUrl={funnelUrl}
            operatorName={operatorData.operatorName}
            sourceCityName={sourceCityName}
            destinationCityName={destinationCityName}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={false}
            demoLink={true}
          />
        </div>
        <div className={`mobileShow makeSticky`}>
          <BusModifySearch
            funnelUrl={funnelUrl}
            operatorName={operatorData.operatorName}
            sourceCityName={sourceCityName}
            destinationCityName={destinationCityName}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={true}
            demoLink={true}
          />
        </div>

        <main className="container">
          <BreadCrumb
            breadcrumb={templateData.breadcrumb}
            type={`_Route_Operator`}
          />
          <PageHeader
            title={templateData.h1Title}
            titleReplaceMent={
              rtcAcronym
                ? `${sourceCityName} to ${destinationCityName} ${rtcAcronym} Bus Booking`
                : ''
            }
            titleFirst={
              templateData.localBusTemplate
                ? templateData.localBusTemplate
                : false
            }
          />
          {
            <AdsComponent
              template={'Route_Operator'}
              placedAt={'top'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: null },
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom20 adsLayout',
                style: { marginTop: null },
              }}
            />
          }
          <br style={{ display: 'none' }} />
          <BusBookingSummary bookingSummary={bookingSummary} />
          {topBusesFrom &&
            topBusesFrom.categorizedBusGroups &&
            topBusesFrom.categorizedBusGroups.length > 0 && (
              <>
                <BusDetailsList
                  noOfListings={
                    templateData.noOfListings ? templateData.noOfListings : 10
                  }
                  titlePrefix={`Top ${displayedOperatorName} Buses `}
                  type={'_Route_Operator'}
                  busGroups={topBusesFrom.categorizedBusGroups}
                  route={`from ${sourceCityName} to ${destinationCityName}`}
                  onBusClicked={onBusClicked}
                  setcollapsableFlag={setcollapsableFlag}
                  setWidget={setWidget}
                  isMobileDevice={isMobileDevice}
                  fromCity={fromCity}
                  toCity={toCity}
                  fromCode={fromCode}
                  toCode={toCode}
                  selectedDay={date}
                  operatorName={operatorName}
                />
                <br />
              </>
            )}
          {topBusesFrom &&
            topBusesFrom.otherBusGroups &&
            topBusesFrom.otherBusGroups.length > 0 && (
              <>
                <BusDetailsList
                  noOfListings={
                    templateData.noOfListings ? templateData.noOfListings : 10
                  }
                  titlePrefix={`Other Buses `}
                  type={'_Route_Operator'}
                  busGroups={topBusesFrom.otherBusGroups}
                  route={`from ${sourceCityName} to ${destinationCityName}`}
                  onBusClicked={onBusClicked}
                  setcollapsableFlag={setcollapsableFlag}
                  setWidget={setWidget}
                  isMobileDevice={isMobileDevice}
                  fromCity={fromCity}
                  toCity={toCity}
                  fromCode={fromCode}
                  toCode={toCode}
                  selectedDay={date}
                />
                <br />
              </>
            )}
          {returnRouteOperatorUrl && (
            <ReturnBusSection
              type="Route_Operator"
              sourceCityName={sourceCityName}
              destinationCityName={destinationCityName}
              categoryName={displayedOperatorName}
              url={returnRouteOperatorUrl}
            />
          )}
          {
            <AdsComponent
              template={'Route_Operator'}
              placedAt={'middle'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: '-20px' },
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom10 adsLayout',
                style: { marginTop: '-5px' },
              }}
            />
          }
          {topOperatorsList && topOperatorsList.length > 0 && (
            <section>
              <div
                style={{ display: 'none' }} // disabled in SSR HTML
                className="starRatngWrap__top--mob paddingR16 visibilityAuto"
              >
                <p className="font22 latoLight appendBottom2">Top Operators</p>
                <p className="font16 latoBold lineHeight20 blackText appendBottom5">
                  {`from ${sourceCityName} to ${destinationCityName}`}
                </p>
              </div>
              <div className="starRatngWrap__top--desk">
                <p>
                  {`Top Operators for ${sourceCityName} to ${destinationCityName} buses`}
                </p>
              </div>
              <StarRatingBox topOperatorsList={topOperatorsList} />
            </section>
          )}
          {templateData.extraInfo && (
            <section>
              <h2
                className={`paddingR16 visibilityAuto`}
              >{`Book ${displayedOperatorName} Buses from ${sourceCityName} to ${destinationCityName}`}</h2>
              <div
                className="infoCard"
                dangerouslySetInnerHTML={{ __html: templateData.extraInfo }}
              ></div>
            </section>
          )}
          {operatorData.description && (
            <section>
              <ThingsToKnow
                title={'Things To Know About'}
                text={operatorData.description}
                cityName={displayedOperatorName}
              />
            </section>
          )}

          <div className={`busSeoWrap__Details makeFlex visibilityAuto`}>
            {sourceInterlinkingBlockData.map((item, idx) => {
              const { mainData, titlePrefix, titleSuffix } = item;
              if (!mainData || mainData?.length < 1) {
                return;
              } else {
                return (
                  <CompactListV1
                    blockData={mainData}
                    titlePrefix={titlePrefix}
                    titleSuffix={titleSuffix}
                    key={idx}
                  />
                );
              }
            })}
          </div>
          {sourceCityData.description && (
            <section>
              <ThingsToKnow
                text={sourceCityData.description}
                title={'Things To Know About'}
                cityName={sourceCityName}
              />
            </section>
          )}

          <div className={`busSeoWrap__Details makeFlex visibilityAuto`}>
            {destinationInterlinkingBlockData.map((item, idx) => {
              const { mainData, titlePrefix, titleSuffix } = item;
              if (!mainData || mainData?.length < 1) {
                return;
              } else {
                return (
                  <CompactListV1
                    blockData={mainData}
                    titlePrefix={titlePrefix}
                    titleSuffix={titleSuffix}
                    key={idx}
                  />
                );
              }
            })}
          </div>
          {destinationCityData.description && (
            <section>
              <ThingsToKnow
                title={'Things To Know About'}
                text={destinationCityData.description}
                cityName={destinationCityName}
              />
            </section>
          )}

          {templateData && templateData.faq && templateData.faq.length > 0 && (
            <section className="visibilityAuto">
              <Faq
                data={templateData.faq}
                getListingUrl={getListingUrl}
                isMobileDevice={isMobileDevice}
                pageUrl={templateData.sampleUrl}
                type={'_Route_Operator'}
              />
            </section>
          )}
        </main>
        {!isMobileDevice &&
          openOffersDropdown &&
          hasDropdownOpened &&
          seoOfferData.length > 0 && (
            <OfferDropdown
              seoOfferData={seoOfferData}
              setOpenOffersDropdown={setOpenOffersDropdown}
            />
          )}
        {templateData && <Footer template={templateData} />}
        <div
          className="blackOverlay"
          onClick={() => {
            setcollapsableFlag(true);
          }}
        ></div>
      </div>
    </AppContextProvider>
  );
};

RouteOperatorTemplate.fetchData = async (_, match, urlObj, gRPCClient) => {
  const {
    params: { routeOperatorId },
  } = match;
  const url = `${DATA_LOADER_URL}/api/routeOperatorTemplateData/${routeOperatorId}?${urlObj.query}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(
      `Unable to fetch route category details of ${routeOperatorId}`,
      responseData.error,
    );
  }
  return responseData;
  // return await gRPCClient.getBusCityTemplateDate(cityCode, 'dynamic');
};
export default RouteOperatorTemplate;
